import { z } from "zod";
import { PaginationQuerySchema } from "./common.js";
// ─── Email (Tracked Email, formerly DocEditor) ─────────────
// Persisted tracked-email rows can include legacy/imported or synthetic
// actor identifiers. Input schemas still require deliverable email shapes;
// response schemas must not crash a list view because of one old sender.
const PersistedEmailActorSchema = z.string().min(1).max(320);
const EmailRecipientSchema = z.object({
    email: z.string().email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
});
export const EmailAttachmentRefSchema = z.object({
    documentId: z.string().uuid(),
    filename: z.string().min(1).max(500),
    mimeType: z.string().min(1).max(100),
    sizeBytes: z.number().int().nonnegative(),
});
const EmailBounceDetailSchema = z.object({
    email: PersistedEmailActorSchema,
    reason: z.string().max(500).nullable().default(null),
    diagnosticCode: z.string().max(1000).nullable().default(null),
    source: z.string().max(80).default("provider"),
    bouncedAt: z.string().datetime(),
});
/**
 * How multi-recipient tracked sends are addressed:
 * - `individual` (default): one Graph message per recipient (per-person
 *   open/click/unsubscribe). Footer lists co-addressees when there are several.
 * - `shared`: one Graph message with everyone on To (Reply-All works).
 *   Engagement pixels are not personalised — open/click attribution is skipped.
 */
export const EmailAddressingModeSchema = z.enum(["individual", "shared"]);
export const TrackedEmailSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid().nullable().default(null),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string(),
    status: z.enum(["draft", "sending", "sent", "failed"]),
    sendMethod: z.enum(["graph", "draft", "local"]),
    senderEmail: PersistedEmailActorSchema,
    recipientCount: z.number().int(),
    sentToEmails: z.array(PersistedEmailActorSchema).default([]),
    sentAt: z.string().datetime().nullable(),
    sendResults: z
        .object({
        total: z.number().int(),
        sent: z.number().int(),
        failed: z.number().int(),
        bounced: z.number().int().nonnegative().default(0),
        errors: z.array(z.object({
            email: PersistedEmailActorSchema,
            error: z.string(),
        })),
        bounces: z.array(EmailBounceDetailSchema).default([]),
        addressingMode: EmailAddressingModeSchema.optional(),
    })
        .nullable(),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: PersistedEmailActorSchema.nullable().default(null),
    attachments: z.array(EmailAttachmentRefSchema).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const EmailListQuerySchema = PaginationQuerySchema.extend({
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
export const EmailSendSchema = z.object({
    to: z.array(EmailRecipientSchema).min(1).max(500),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1),
    attachments: z.array(EmailAttachmentRefSchema).max(25).default([]),
    addressingMode: EmailAddressingModeSchema.default("individual"),
});
export const EmailDraftSaveSchema = z.object({
    to: z.array(EmailRecipientSchema).max(500).default([]),
    subject: z.string().trim().min(1).max(500),
    bodyHtml: z.string().min(1),
    attachments: z.array(EmailAttachmentRefSchema).max(25).default([]),
    addressingMode: EmailAddressingModeSchema.default("individual"),
});
export const EmailDraftSendResponseSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(["sent", "failed"]),
    total: z.number().int(),
    sent: z.number().int(),
    failed: z.number().int(),
    errors: z.array(z.object({
        email: PersistedEmailActorSchema,
        error: z.string(),
    })),
});
export const EmailBounceReportSchema = z.object({
    emailId: z.string().uuid(),
    recipientEmail: z.string().email().toLowerCase(),
    reason: z.string().trim().max(500).nullable().optional(),
    diagnosticCode: z.string().trim().max(1000).nullable().optional(),
    source: z.string().trim().min(1).max(80).default("provider"),
    occurredAt: z.string().datetime().nullable().optional(),
});
export const EmailBounceReportBodySchema = EmailBounceReportSchema.omit({ emailId: true });
export const EmailBounceWebhookSchema = EmailBounceReportSchema.extend({
    eventId: z.string().trim().min(1).max(200).optional(),
});
export const EmailBounceSyncRequestSchema = z.object({
    mailbox: z.string().email().toLowerCase().optional(),
    folder: z.string().trim().min(1).max(200).optional().default("Inbox"),
    lookbackMinutes: z.coerce.number().int().min(1).max(10_080).optional().default(1_440),
    limit: z.coerce.number().int().min(1).max(50).optional().default(25),
    dryRun: z.boolean().optional().default(false),
});
export const EmailBounceSyncResponseSchema = z.object({
    mailbox: z.string().email(),
    folder: z.string(),
    lookbackMinutes: z.number().int().positive(),
    limit: z.number().int().positive(),
    dryRun: z.boolean(),
    scanned: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    recorded: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    skipped: z.array(z.object({
        messageId: z.string(),
        subject: z.string().nullable(),
        reason: z.string(),
    })),
    bounces: z.array(z.object({
        messageId: z.string(),
        emailId: z.string().uuid(),
        recipientEmail: z.string().email(),
        reason: z.string().nullable(),
        diagnosticCode: z.string().nullable(),
        occurredAt: z.string().datetime(),
        duplicate: z.boolean(),
    })),
});
// ─── Email-scoped attachment upload (SALES-accessible routes) ─
/**
 * Body for POST /email/attachments/upload-token.
 * Server resolves the folder; caller supplies only file identity.
 */
export const EmailAttachmentUploadTokenSchema = z.object({
    filename: z.string().min(1).max(500),
    contentType: z.string().min(1).max(200),
});
/**
 * Body for POST /email/attachments/document.
 * Registers a vault document after the raw blob PUT completes.
 */
export const EmailAttachmentRegisterSchema = z.object({
    blobKey: z.string().min(1).max(1024),
    filename: z.string().min(1).max(500),
    mimeType: z.string().min(1).max(200),
    sizeBytes: z.number().int().nonnegative(),
});
// ─── Tracking Events ───────────────────────────────────────
export const EmailTrackingEventSchema = z.object({
    id: z.string().uuid(),
    emailId: z.string().uuid(),
    recipientEmail: z.string().email(),
    eventType: z.enum(["open", "click", "optout", "bounce"]),
    linkUrl: z.string().url().nullable(),
    timestamp: z.string().datetime(),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
});
// ─── Contact Email History ─────────────────────────────────
export const EmailHistoryQuerySchema = z.object({
    contactId: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    offset: z.coerce.number().int().min(0).default(0),
});
export const EmailHistoryResponseSchema = z.object({
    emails: z.array(TrackedEmailSchema),
    total: z.number().int(),
    hasMore: z.boolean(),
});
//# sourceMappingURL=email.js.map